import React, { useEffect, useMemo, useState } from 'react';
import api from '../functions/api';
import ThreeTablesModal from './SearchResultModal';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Link
} from '@mui/material';

const TrendsearchTopCompaniesWidget = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  
  // Article modal states
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [articleData, setArticleData] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [selectedTickerForArticles, setSelectedTickerForArticles] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  
  // SearchResultModal states
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get('/trendsearch_ticker_aggregates');
        if (res?.data?.success) {
          setData(res.data.data || []);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load ticker aggregates', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const ranked = useMemo(() => {
    const list = Array.isArray(data) ? [...data] : [];
    list.sort((a, b) => {
      const sa = parseFloat(a.overall_sentiment) || 0;
      const sb = parseFloat(b.overall_sentiment) || 0;
      if (sb !== sa) return sb - sa;
      const tb = (b.total_impacts || 0) - (a.total_impacts || 0);
      if (tb !== 0) return tb;
      return String(a.company_name || '').localeCompare(String(b.company_name || ''));
    });
    return list;
  }, [data]);

  const top3 = ranked.slice(0, 3);

  const handleTickerClick = (ticker, companyName) => {
    if (ticker && ticker !== 'N/A') {
      // If we have a ticker, open the search modal
      setSelectedTicker(ticker);
      setSearchModalOpen(true);
    } else if (companyName) {
      // If no ticker but we have company name, show message
      alert(`No ticker available for ${companyName}. Please populate tickers first.`);
    }
  };

  const handleSearchModalClose = () => {
    setSearchModalOpen(false);
    setSelectedTicker('');
  };

  const formatTimelineShort = (timelineString) => {
    if (!timelineString) return 'N/A';
    // Parse "Immediate (2) | Medium (1) | Long-term (0)" format
    const matches = timelineString.match(/Immediate \((\d+)\).*Medium \((\d+)\).*Long-term \((\d+)\)/);
    if (matches) {
      const [, immediate, medium, longTerm] = matches;
      return `I: ${immediate}, M: ${medium}, LT: ${longTerm}`;
    }
    return timelineString;
  };

  const handleArticleClick = async (ticker, impactType, column) => {
    try {
      setLoadingArticles(true);
      // DAS-2957: the endpoint defaults to the last 30 days, but the W/L/E/MC counts
      // are all-time and "Recent" is a 7-day count. Ask for the window that matches
      // the number the user clicked, so the list has as many rows as the count.
      const params = { ticker, days: column === 'recent_activity_7d' ? 7 : 3650 };

      // Add impact type filter based on column
      if (column === 'winner_count') params.impact_type = 'company_winner';
      else if (column === 'loser_count') params.impact_type = 'company_loser';
      else if (column === 'enabler_count') params.impact_type = 'company_enabler';
      else if (column === 'market_change_count') params.impact_type = 'market_change';
      
      const response = await api.get('/trendsearch_article_details', { params });
      
      if (response.data.success) {
        setArticleData(response.data.data);
        setSelectedTickerForArticles(ticker);
        setSelectedColumn(column);
        setArticleModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoadingArticles(false);
    }
  };

  const smallItem = (item, idx) => {
    const score = parseFloat(item.overall_sentiment) || 0;
    const color = score >= 2 ? '#4caf50' : score >= -2 ? '#ff9800' : '#f44336';
    return (
      <Box key={`${item.ticker}-${idx}`} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ width: 16, color: '#777' }}>{idx + 1}</Typography>
          <Chip label={item.ticker || 'N/A'} size="small" color="primary" variant="outlined" />
          <Typography variant="body2" noWrap title={item.company_name} sx={{ maxWidth: 150 }}>
            {item.company_name}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color }}>{score.toFixed(2)}</Typography>
      </Box>
    );
  };

  const columns = [
    { id: 'rank', label: '#', width: 40 },
    { id: 'ticker', label: 'Ticker', width: 80 },
    { id: 'company_name', label: 'Company', width: 220 },
    { id: 'sector', label: 'Sector', width: 140 },
    { id: 'industry', label: 'Industry', width: 180 },
    { id: 'overall_sentiment', label: 'Sentiment', width: 90 },
    { id: 'total_impacts', label: 'Total', width: 70 },
    // DAS-2943: articles still carrying weight in the score (faded ones have dropped out)
    { id: 'active_impacts', label: 'Active', width: 70 },
    { id: 'winner_count', label: 'W', width: 50 },
    { id: 'loser_count', label: 'L', width: 50 },
    { id: 'enabler_count', label: 'E', width: 50 },
    { id: 'market_change_count', label: 'MC', width: 60 },
    { id: 'trend_timeline', label: 'Effect', width: 150 },
    { id: 'recent_activity_7d', label: 'Recent', width: 70 },
    { id: 'latest_impact_date', label: 'Latest', width: 110 }
  ];

  return (
    <>
      <Card variant="outlined" onClick={() => setOpen(true)} sx={{ cursor: 'pointer' }} data-testid="trendsearch-top-companies-card">
        <CardContent sx={{ p: 1.25 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
            Top Companies (TrendSearch)
          </Typography>
          {loading ? (
            <Typography variant="body2">Loading…</Typography>
          ) : top3.length === 0 ? (
            <Typography variant="body2">No data</Typography>
          ) : (
            <Box>
              {top3.map((item, idx) => smallItem(item, idx))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth={false} data-testid="trendsearch-ranking-modal">
        <DialogTitle>Company Ranking (TrendSearch)</DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} sx={{ maxHeight: 600, width: '100%', overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c.id} style={{ width: c.width, minWidth: c.width, maxWidth: c.width, fontWeight: 'bold', fontSize: 12 }}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ranked.map((row, idx) => (
                  <TableRow key={`${row.ticker}-${idx}`}>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>{idx + 1}</TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Chip 
                        label={row.ticker} 
                        color="primary" 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleTickerClick(row.ticker, row.company_name)}
                        style={{ cursor: row.ticker && row.ticker !== 'N/A' ? 'pointer' : 'default' }}
                      />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      {row.company_name}
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Chip label={row.sector} color="secondary" size="small" variant="outlined" />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>{row.industry}</TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <span style={{ color: (parseFloat(row.overall_sentiment) || 0) >= 2 ? '#4caf50' : (parseFloat(row.overall_sentiment) || 0) >= -2 ? '#ff9800' : '#f44336', fontWeight: 'bold' }}>
                          {(parseFloat(row.overall_sentiment) || 0).toFixed(2)}
                        </span>
                        <span>{row.sentiment_indicator}</span>
                      </Box>
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Chip label={row.total_impacts} size="small" variant="outlined" title="All articles ever recorded for this company" />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }} data-testid="trendsearch-active-impacts">
                      <Chip
                        label={row.active_impacts ?? '-'}
                        size="small"
                        variant="outlined"
                        title="Articles still counting in the score (an article fades out over 6 months)"
                      />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Chip 
                        label={row.winner_count} 
                        color="success" 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleArticleClick(row.ticker, 'company_winner', 'winner_count')}
                        style={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Chip 
                        label={row.loser_count} 
                        color="error" 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleArticleClick(row.ticker, 'company_loser', 'loser_count')}
                        style={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Chip 
                        label={row.enabler_count} 
                        color="primary" 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleArticleClick(row.ticker, 'company_enabler', 'enabler_count')}
                        style={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Chip 
                        label={row.market_change_count} 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleArticleClick(row.ticker, 'market_change', 'market_change_count')}
                        style={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }} title={row.trend_timeline}>{formatTimelineShort(row.trend_timeline)}</TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      {row.recent_activity_7d > 0 ? (
                        <Chip 
                          label={row.recent_activity_7d} 
                          color="warning" 
                          size="small" 
                          variant="outlined"
                          onClick={() => handleArticleClick(row.ticker, null, 'recent_activity_7d')}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <span style={{ color: '#757575' }}>0</span>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: '4px 2px', fontSize: 12 }}>
                      <Typography 
                        variant="body2" 
                        onClick={() => handleArticleClick(row.ticker, null, 'latest_impact_date')}
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      >
                        {row.latest_impact_date}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Article Details Modal */}
      <Dialog
        open={articleModalOpen}
        onClose={() => setArticleModalOpen(false)}
        maxWidth="lg"
        fullWidth
        data-testid="trendsearch-articles-modal"
        PaperProps={{
          style: {
            minHeight: '60vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Articles for {selectedTickerForArticles} - {selectedColumn === 'winner_count' ? 'Winners' : 
                                                        selectedColumn === 'loser_count' ? 'Losers' : 
                                                        selectedColumn === 'enabler_count' ? 'Enablers' : 
                                                        selectedColumn === 'market_change_count' ? 'Market Changes' : 
                                                        selectedColumn === 'recent_activity_7d' ? 'Recent Activity' : 
                                                        'Latest Articles'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingArticles ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading articles...
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Impact</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {articleData.map((article, index) => (
                    <TableRow key={index}>
                      <TableCell>{article.date}</TableCell>
                      <TableCell>
                        {article.article_url ? (
                          <Link
                            href={article.article_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            color="primary"
                          >
                            {article.article_title || 'View Article'}
                          </Link>
                        ) : (
                          article.article_title || 'No title'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={article.impact_type === 'company_winner' ? 'Winner' :
                                 article.impact_type === 'company_loser' ? 'Loser' :
                                 article.impact_type === 'company_enabler' ? 'Enabler' :
                                 'Market Change'}
                          color={article.impact_type === 'company_winner' ? 'success' :
                                 article.impact_type === 'company_loser' ? 'error' :
                                 article.impact_type === 'company_enabler' ? 'primary' :
                                 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={article.full_description}>
                          {article.first_5_words || article.full_description?.substring(0, 50) + '...' || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>{article.source_name || 'Unknown'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArticleModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* SearchResultModal for ticker clicks */}
      <ThreeTablesModal
        tickerValue={selectedTicker}
        open={searchModalOpen}
        onClose={handleSearchModalClose}
        userId={null}
      />
    </>
  );
};

export default TrendsearchTopCompaniesWidget;


